/**
 * Admin API - Admin-only endpoints for user management
 * Requires Admin role
 */
import apiClient from './client';
import { User } from './auth.api';

// Types
export interface AdminUser extends User {
    tel?: string;
    pinFailedAttempts: number;
    lastPinAttempt?: string;
    pinSetupToken?: string;
    pinResetToken?: string;
    devices: DeviceInfo[];
}

export interface DeviceInfo {
    id: string;
    deviceName: string;
    isVerified: boolean;
    isBlocked: boolean;
    biometricEnabled: boolean;
    lastUsed: string;
    ipAddress: string;
}

export interface CreateUserRequest {
    telephone: string;
    email?: string;
    role: 'Admin' | 'SuperAgent' | 'Agent' | 'Caissier' | 'User';
}

export interface UsersListResponse {
    users: AdminUser[];
    total: number;
    page: number;
    limit: number;
}

export interface FlaggedDevice {
    id: string;
    deviceName: string;
    userId: string;
    userName: string;
    reason: string;
    ipAddress: string;
    flaggedAt: string;
}

// API Functions
export const adminApi = {
    /**
     * List all users (Admin only)
     */
    async listUsers(params?: {
        page?: number;
        limit?: number;
        role?: string;
        search?: string;
    }): Promise<UsersListResponse> {
        const response = await apiClient.get('/api/auth/admin/users', { params });
        return response.data;
    },

    /**
     * Get single user by ID (Admin only)
     */
    async getUser(userId: string): Promise<AdminUser> {
        const response = await apiClient.get(`/api/auth/users/${userId}`);
        return response.data;
    },

    /**
     * Prepare a user (Admin creates user without PIN)
     */
    async prepareUser(data: CreateUserRequest): Promise<AdminUser> {
        const response = await apiClient.post('/api/auth/prepare-user', data);
        return response.data;
    },

    /**
     * Activate user
     */
    async activateUser(userId: string): Promise<{ message: string }> {
        const response = await apiClient.put(`/api/auth/users/${userId}/activate`);
        return response.data;
    },

    /**
     * Deactivate user
     */
    async deactivateUser(userId: string): Promise<{ message: string }> {
        const response = await apiClient.put(`/api/auth/users/${userId}/deactivate`);
        return response.data;
    },

    /**
     * Delete user (soft delete)
     */
    async deleteUser(userId: string): Promise<{ message: string }> {
        const response = await apiClient.delete(`/api/auth/users/${userId}`);
        return response.data;
    },

    /**
     * Resend PIN setup link
     */
    async resendPinSetup(params: {
        userId?: string;
        telephone?: string;
        email?: string;
    }): Promise<{ message: string }> {
        const response = await apiClient.post('/api/auth/users/resend-pin-setup', {
            ...params,
            frontendUrl: window.location.origin,
        });
        return response.data;
    },

    /**
     * Block device
     */
    async blockDevice(deviceId: string, reason: string): Promise<{ message: string }> {
        const response = await apiClient.post('/api/auth/devices/block', { deviceId, reason });
        return response.data;
    },

    /**
     * Unblock device
     */
    async unblockDevice(deviceId: string): Promise<{ message: string }> {
        const response = await apiClient.post('/api/auth/devices/unblock', { deviceId });
        return response.data;
    },

    /**
     * Get flagged devices
     */
    async getFlaggedDevices(): Promise<FlaggedDevice[]> {
        const response = await apiClient.get('/api/auth/devices/flagged');
        return response.data;
    },

    // ============================================
    // DASHBOARD ANALYTICS
    // ============================================

    /**
     * Get comprehensive dashboard statistics
     */
    async getDashboardStats(period: 'today' | 'week' | 'month' | 'year' | 'all' = 'all'): Promise<DashboardStatsResponse> {
        const response = await apiClient.get('/api/dashboard/analytics/dashboard-stats', {
            params: { period }
        });
        return response.data;
    },

    /**
     * Get KPIs only
     */
    async getKPIs(period: 'today' | 'week' | 'month' | 'year' | 'all' = 'all'): Promise<DashboardStatsResponse['kpis']> {
        const response = await apiClient.get('/api/dashboard/analytics/kpis', {
            params: { period }
        });
        return response.data;
    },

    /**
     * Get monthly overview chart data
     */
    async getOverviewData(): Promise<{ monthly_data: MonthlyData[] }> {
        const response = await apiClient.get('/api/dashboard/analytics/overview');
        return response.data;
    },

    /**
     * Get recent transactions for dashboard
     */
    async getRecentTransactions(limit: number = 10): Promise<RecentTransaction[]> {
        const response = await apiClient.get('/api/dashboard/analytics/recent-transactions', {
            params: { limit }
        });
        return response.data;
    },

    /**
     * Get SOL account progress
     */
    async getSolProgress(limit: number = 20, page: number = 1): Promise<PaginatedSolProgress> {
        const response = await apiClient.get('/api/dashboard/analytics/sol-progress', {
            params: { limit, page }
        });
        return response.data;
    },

    /**
     * Get system summary counts
     */
    async getSystemSummary(): Promise<SystemSummary> {
        const response = await apiClient.get('/api/dashboard/analytics/summary');
        return response.data;
    },
};

// ============================================
// DASHBOARD ANALYTICS TYPES
// ============================================

export interface KPI {
    value: number;
    growth: number;
    currency?: string;
    label?: string;
}

export interface MonthlyData {
    month: string;
    total: number;
    deposits?: number;
    withdrawals?: number;
}

export interface RecentTransaction {
    id: string;
    client_name: string;
    client_initials: string;
    email?: string;
    phone?: string;
    amount: number;
    formatted_amount: string;
    type: string;
    type_display: string;
    created_at: string;
}

export interface SolProgress {
    compte_id: string;
    compte_code: string;
    client_name: string;
    plan: string;
    semaines_payees: number;
    total_semaines: number;
    duree_totale: number;
    frequence: string;
    progress_percent: number;
    est_a_jour: boolean;
    montant_non_paye: number;
    solde_solidere: number;
    bonis: number;
    devise: string;
    derniere_transaction?: string;
}

export interface SystemSummary {
    clients: {
        total: number;
        active: number;
        pending_kyc: number;
        verified: number;
        rejected_kyc: number;
    };
    comptes: {
        total: number;
        bs: number;
        sol: number;
        active: number;
        inactive: number;
    };
    agents: {
        total: number;
        active: number;
        with_clients: number;
    };
    transactions: {
        today: number;
        this_week: number;
        this_month: number;
    };
}

export interface DashboardStatsResponse {
    kpis: {
        total_depots: KPI;
        nouveaux_comptes: KPI;
        total_transactions: KPI;
        comptes_actifs: KPI;
    };
    overview: {
        monthly_data: MonthlyData[];
    };
    recent_transactions: RecentTransaction[];
    sol_progress: PaginatedSolProgress;
    summary: SystemSummary;
    generated_at: string;
}

export interface PaginatedSolProgress {
    data: SolProgress[];
    meta: {
        total: number;
        page: number;
        limit: number;
        last_page: number;
    };
}

export default adminApi;

